import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import mapboxgl from "mapbox-gl";
import * as THREE from "three";
import { useState, useEffect, useRef } from "react";

mapboxgl.accessToken = "pk.eyJ1IjoicmFtOTYiLCJhIjoiY204d3hwb3FoMDA1cjJrc2FkZHJ1emt4ciJ9.9nQgOHgvYo9GRF7hmGheww";

const cities = [
  { name: "New York", lat: 19.0760, lon: 72.8777 },
  { name: "London", lat: 51.5074, lon: -0.1278 },
  { name: "Tokyo", lat: 35.6895, lon: 139.6917 },
];

const mumbaiCoords = [72.8777, 19.0760];

function latLonToXYZ(lat, lon, radius = 2) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const y = radius * Math.cos(phi);
  const z = radius * Math.sin(phi) * Math.sin(theta);
  return [x, y, z];
}

function CityScene({ city }) {
  const testCity = { name: "Delhi", lat: 28.7041, lon: 77.1025 }; // Default city for testing
  city = city || testCity; // Use test city if none is selected

  const mapContainer = useRef(null);
  const map = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "",
      center: [city.lon, city.lat],
      zoom: 15,
      pitch: 60,
      bearing: -17.6,
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [city]);

  useEffect(() => {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 5, 10);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    document.body.appendChild(renderer.domElement);

    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;

    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current.domElement.remove();
        rendererRef.current = null;
      }
    };
  }, []);

  return <div ref={mapContainer} style={{ width: "100vw", height: "100vh" }} />;
}

function Globe({ setView, setSelectedCity }) {
  const [selectedCity, setLocalSelectedCity] = useState(null);
  const cameraRef = useRef();

  useFrame(({ camera }) => {
    if (selectedCity) {
      camera.position.lerp(new THREE.Vector3(0, 3, 3), 0.05);
      if (camera.position.distanceTo(new THREE.Vector3(0, 3, 3)) < 0.1) {
        setView("city");
        setSelectedCity(selectedCity);
      }
    }
  });

  return (
    <>
      <ambientLight intensity={0.5} />
      <pointLight position={[10, 10, 10]} intensity={1} />

      {/* Globe */}
      <mesh visible={!selectedCity}>
        <sphereGeometry args={[2, 64, 64]} />
        {/* <meshStandardMaterial
          map={new THREE.TextureLoader().load("/earth-texture.jpg")}
        /> */}
      </mesh>

      {/* Markers */}
      {cities.map((city, index) => {
        const [x, y, z] = latLonToXYZ(city.lat, city.lon);
        return (
          <mesh
            key={index}
            position={[x, y, z]}
            onClick={() => setLocalSelectedCity(city)}
          >
            <sphereGeometry args={[0.05, 16, 16]} />
            <meshStandardMaterial color="red" />
            <Html position={[0, 0.1, 0]}>{city.name}</Html>
          </mesh>
        );
      })}

      <OrbitControls />
    </>
  );
}

export default function GlobeApp() {
  const [view, setView] = useState("globe");
  const [selectedCity, setSelectedCity] = useState(null);

  return (
    <div style={{ height: "100vh", width: "100vw" }}>
      {view === "globe" ? (
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Globe setView={setView} setSelectedCity={setSelectedCity} />
        </Canvas>
      ) : (
        <CityScene city={selectedCity} />
      )}
    </div>
  );
}